import React, { useEffect, useRef } from 'react'; // Importamos useEffect y useRef
import { 
    Cog6ToothIcon, 
    PlayCircleIcon, 
    SpeakerWaveIcon, 
    BookOpenIcon, 
    ClockIcon, 
    ArrowRightIcon 
} from '@heroicons/react/24/solid';
import { PHASES, SOUND_OPTIONS } from './constants';
import CustomToggle from './CustomToggle';
import ContactSection from '../../../common/ContactSection';

const RevelationCard = ({ revelation }) => {
    const date = new Date(revelation.DateCreated);
    const formattedDate = date.toLocaleDateString('es-ES', { year: 'numeric', month: 'long', day: 'numeric' });
    const minutesMeditated = Math.floor(revelation.TotalTimeMeditated / 60);

    return (
        <div className="p-4 bg-gray-800/50 border border-gray-700 rounded-lg">
            <p className="text-gray-200 whitespace-pre-wrap">{revelation.Revelation}</p>
            <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-700/50 text-xs text-gray-400">
                <span>{formattedDate}</span>
                <span className="flex items-center gap-1.5"><ClockIcon className="w-4 h-4" /> {minutesMeditated} min</span>
            </div>
        </div>
    );
};

const MeditationSettings = ({
    settings,
    phaseDurations,
    activePhases,
    activeGuidedMeditations,
    isPremium,
    backgroundVolume,
    revelations,
    onSettingsChange,
    onDurationChange,
    onPhaseToggle,
    onGuidedToggle,
    onVolumeChange,
    onStart,
    onProceed,
    nextActivity,
    forcePresenceMode = false // NUEVA PROP: Indica si venimos en modo directo de Presencia
}) => {
    
    // Usamos un ref para asegurarnos de que el auto-start solo ocurra una vez al montar
    const hasAutoStarted = useRef(false);

    useEffect(() => {
        if (forcePresenceMode && !hasAutoStarted.current) {
            hasAutoStarted.current = true;
            
            // 1. Configurar las fases: Activar Presencia (index 0) y desactivar las demás
            activePhases.forEach((isActive, index) => {
                if (index === 0 && !isActive) {
                    onPhaseToggle(index); // Activa Presencia si no lo está
                } else if (index !== 0 && isActive) {
                    onPhaseToggle(index); // Desactiva las demás si están activas
                }
            });
            
            // 1. Configurar las fases: Activar Presencia (index 0) y desactivar las demás
            activePhases.forEach((isActive, index) => {
                if (index === 0 && !isActive) {
                    onGuidedToggle(index); // Activa Presencia si no lo está
                } else if (index !== 0 && isActive) {
                    onGuidedToggle(index); // Desactiva las demás si están activas
                }
            });

            // 2. Iniciar la meditación automáticamente
            // Usamos un pequeño timeout para asegurar que el estado de las fases se procese 
            // (dependiendo de cómo maneje el padre el estado de activePhases)
            setTimeout(() => {
                onStart();
            }, 100);
        }
    }, [forcePresenceMode]);

    const handleSelectChange = (e) => {
        const { name, value } = e.target;
        const optionsList = name === 'gong' ? SOUND_OPTIONS.gongs : SOUND_OPTIONS.background;
        const selectedOption = optionsList.find(opt => opt.id === value);

        if (selectedOption && selectedOption.isPremium && !isPremium) {
            alert("Esta es una opción premium. Debes mejorar tu cuenta para usarla.");
            return;
        }
        onSettingsChange(name, value);
    };
    
    const atLeastOnePhaseActive = activePhases.some(isActive => isActive);

    return (
        <div className="w-full mx-auto p-4 sm:p-6 md:p-8 bg-gray-900 rounded-xl border border-gray-700 animate-fade-in">
            <div className="text-center mb-10">
                <Cog6ToothIcon className="w-12 h-12 text-purple-400 mx-auto mb-4" />
                <h1 className="text-3xl md:text-4xl font-bold text-white">Meditación de 7 Fases</h1>
                <p className="text-gray-400 mt-2 max-w-md mx-auto">Configura tu experiencia para alinearla con tu intención de hoy.</p>
            </div>

            <div className="mb-12">
                <h2 className="text-2xl font-semibold text-yellow-400 mb-5">Fases de la Meditación</h2>
                <div className="space-y-4">
                    {PHASES.map((phase, index) => (
                        <div key={phase.id} className="p-4 rounded-lg bg-gray-800/70 border border-gray-700">
                            <CustomToggle id={`toggle-${phase.id}`} label={phase.name} isOn={activePhases[index]} handleToggle={() => onPhaseToggle(index)} />

                            <div className={`transition-opacity duration-300 ${activePhases[index] ? 'opacity-100' : 'opacity-40 pointer-events-none'}`}>
                                <div className="flex items-center gap-2 sm:gap-4 mt-4">
                                    <input type="range" min="3" max="15" step="1" value={phaseDurations[index]} onChange={(e) => onDurationChange(index, parseInt(e.target.value, 10))} className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-purple-500" />
                                    <span className="text-purple-300 font-mono w-16 text-right text-sm sm:text-base">{phaseDurations[index]} min</span>
                                </div>
                                <div className="mt-4 pt-3 border-t border-gray-700/50">
                                     <CustomToggle id={`toggle-guided-${phase.id}`} label="Meditación Guiada para esta fase" isOn={activeGuidedMeditations[index]} handleToggle={() => onGuidedToggle(index)} />
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            <div className="mb-12">
                <h2 className="text-2xl font-semibold text-yellow-400 mb-5">Ajustes Generales de Audio</h2>
                <div className="space-y-4">
                    <div className="p-4 rounded-lg bg-gray-800/70 border border-gray-700">
                        <CustomToggle id="enableIntro" label="Escuchar Introducción" isOn={settings.enableIntro} handleToggle={() => onSettingsChange('enableIntro', !settings.enableIntro)} />
                    </div>
                    <div className="p-4 rounded-lg bg-gray-800/70 border border-gray-700">
                        <label htmlFor="background" className="block text-white font-bold mb-2">Música de Fondo</label>
                        <select name="background" id="background" value={settings.background} onChange={handleSelectChange} className="w-full p-2.5 bg-gray-700 text-white rounded-lg border-gray-600 focus:ring-2 focus:ring-purple-500 focus:border-purple-500">
                           {SOUND_OPTIONS.background.map(opt => <option key={opt.id} value={opt.id} disabled={opt.isPremium && !isPremium}>{opt.name} {opt.isPremium ? '(Premium)' : ''}</option>)}
                        </select>
                    </div>
                    <div className="p-4 rounded-lg bg-gray-800/70 border border-gray-700">
                        <label htmlFor="volume" className="block text-white font-bold mb-2">Volumen de la Música de Fondo</label>
                        <div className="flex items-center gap-4">
                            <SpeakerWaveIcon className="w-6 h-6 text-purple-400"/>
                            <input id="volume" type="range" min="0" max="1" step="0.05" value={backgroundVolume} onChange={(e) => onVolumeChange(e.target.value)} className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-purple-500" />
                        </div>
                    </div>
                    <div className="p-4 rounded-lg bg-gray-800/70 border border-gray-700">
                        <label htmlFor="gong" className="block text-white font-bold mb-2">Sonido de Transición</label>
                        <select name="gong" id="gong" value={settings.gong} onChange={handleSelectChange} className="w-full p-2.5 bg-gray-700 text-white rounded-lg border-gray-600 focus:ring-2 focus:ring-purple-500 focus:border-purple-500">
                             {SOUND_OPTIONS.gongs.map(opt => <option key={opt.id} value={opt.id} disabled={opt.isPremium && !isPremium}>{opt.name} {opt.isPremium ? '(Premium)' : ''}</option>)}
                        </select>
                    </div>
                    <div className="p-4 rounded-lg bg-gray-800/70 border border-gray-700">
                        <CustomToggle id="enableGong" label="Activar Gong entre fases" isOn={settings.enableGong} handleToggle={() => onSettingsChange('enableGong', !settings.enableGong)} />
                    </div>
                    <div className="p-4 rounded-lg bg-gray-800/70 border border-gray-700">
                         <CustomToggle id="enableVoice" label="Voz indicativa de fase" isOn={settings.enableVoice} handleToggle={() => onSettingsChange('enableVoice', !settings.enableVoice)} />
                    </div>
                </div>
            </div>

            <div className="text-center mt-6 mb-12">
                <div className="flex flex-col sm:flex-row justify-center items-center gap-4">
                    <button onClick={onStart} disabled={!atLeastOnePhaseActive} className="w-full sm:w-auto inline-flex items-center justify-center gap-3 px-8 py-3 bg-purple-600 text-white font-bold rounded-lg text-lg hover:bg-purple-700 transition-all transform hover:scale-105 disabled:bg-gray-600 disabled:cursor-not-allowed disabled:scale-100 shadow-lg">
                        <PlayCircleIcon className="w-7 h-7"/>
                        Comenzar Meditación
                    </button>
                    
                    {nextActivity && onProceed && (
                        <button onClick={onProceed} className="w-full sm:w-auto bg-yellow-500 text-black font-bold py-3 px-8 rounded-lg hover:bg-yellow-400 transition-all duration-300 flex items-center justify-center gap-2 shadow-lg">
                            Siguiente Actividad
                            <ArrowRightIcon className="w-5 h-5"/>
                        </button>
                    )}
                </div>
                {!atLeastOnePhaseActive && <p className="text-red-400 text-sm mt-2">Debes activar al menos una fase para comenzar.</p>}
            </div>

            <ContactSection />

            {revelations && revelations.length > 0 && (
                 <div className="mt-12 pt-8 border-t border-gray-700">
                    <h2 className="text-2xl font-semibold text-yellow-400 mb-5 text-center flex items-center justify-center gap-3">
                        <BookOpenIcon className="w-7 h-7" />
                        Tus Revelaciones Anteriores
                    </h2>
                    <div className="space-y-4 max-h-96 overflow-y-auto pr-2">
                        {revelations.map(rev => (
                            <RevelationCard key={rev.Id} revelation={rev} />
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default MeditationSettings;